<template>
  <div>
    <div v-if="!showSettings">
      <h4>
        <strong>Warning:</strong> These settings are used for printing via File Drop to a PrintMate
        printer. If your lab does not do this, please disregard these settings, as they may cause
        issues in the app.
      </h4>
      <button class="btn btn-primary mb-2" @click.prevent="enableShowSettings">Configure</button>
    </div>
    <div v-else-if="shouldDisable">
      <h4 class="text-danger">
        File Drop printing has been disabled. Once you click Save, all previous settings will be
        lost.
      </h4>
      <button class="btn btn-success" @click="shouldDisable = false">Undo</button>
    </div>
    <div v-else class="mb-2">
      <text-input v-model="settings.printerName" label="Printer Name" />
      <text-input v-model="settings.url" label="URL" />
      <select-input
        v-model="settings.atAccessioning"
        :items="booleanOptions"
        label="File Drop at Accessioning"
      />
      <text-input v-model="trayOptions" label="Tray Options" @change="setDefaultTray" />
      <select-input
        v-model="settings.defaultTray"
        :items="defaultTrayOptions"
        label="Default Tray"
        valueExpr="displayName"
      />
      <text-input v-model="settings.printProcedure1" label="Procedure 1" />
      <text-input v-model="settings.printProcedure2" label="Procedure 2" />
      <TagInput
        label="Priorities to Print"
        v-model="settings.priorities"
        valueExpr="displayName"
        :items="priorities"
      />
      <div class="border p-2 mt-2">
        <div class="d-flex justify-content-between align-items-end">
          <h4>Protocols to Print</h4>
          <AddButton @click="addProtocolToPrint" />
        </div>
        <div
          class="row"
          v-for="protocol of settings.printProtocols"
          v-bind:key="protocol.protocolPrintId"
        >
          <SelectInput
            class="col-5"
            label="Protocol"
            v-model="protocol.protocolPrintId"
            :items="protocolOptions"
            displayExpr="macroName"
            valueExpr="macroId"
          />
          <TextInput class="col-5" label="Display Text" v-model="protocol.protocolPrintText" />
          <IconButton
            v-tooltip.left="'Remove protocol.'"
            @click="removeProtocolToPrint(protocol)"
            class="m-auto text-danger pointer p-0 col-2"
            :icon="'trash-alt'"
          />
        </div>
      </div>
      <!-- <Checkbox
        label="Denote Single Specimen"
        v-model="settings.denoteSingleSpecimen"
        name="denoteSingleSpecimen"
        id="denoteSingleSpecimen"
      />
      <Checkbox
        label="Denote Single Block"
        v-model="settings.denoteSingleBlock"
        name="denoteSingleBlock"
        id="denoteSingleBlock"
      /> -->
      <button class="btn btn-danger mt-2" @click="handleDisableClick">
        Disable File Drop Printing
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import SelectInput from "../common/SelectInput.vue";
import TagInput from "../common/TagInput.vue";
import TextInput from "../common/TextInput.vue";
import { MacrosApi } from "@/services";
import { MacroTypeEnum } from "@/modules/enums";
import AddButton from "../common/AddButton.vue";
import IconButton from "../common/IconButton.vue";
// import Checkbox from "../common/Checkbox.vue";
export default {
  components: { TextInput, SelectInput, TagInput, AddButton, IconButton },
  props: ["value"],
  data() {
    return {
      settings: {
        printerName: "",
        url: "",
        atAccessioning: 0,
        trayOptions: [],
        defaultTray: "",
        printProcedure1: "",
        printProcedure2: "",
        priorities: [],
        printProtocols: []
        // denoteSingleSpecimen: false,
        // denoteSingleBlock: false
      },
      booleanOptions: [
        { displayName: "Yes", id: 1 },
        { displayName: "No", id: 0 }
      ],
      showSettings: false,
      shouldDisable: false,
      protocolOptions: []
    };
  },
  mounted() {
    if (this.value) {
      this.settings = { ...this.settings, ...JSON.parse(this.value) };
      if (Object.keys(this.settings).length) {
        this.showSettings = true;
      }
      if (this.settings?.protocolPrintId && !this.settings?.printProtocols?.length) {
        this.settings.printProtocols.push({
          protocolPrintId: this.settings.protocolPrintId,
          protocolPrintText: this.settings.protocolPrintText
        });
        delete this.settings.protocolPrintId;
        delete this.settings.protocolPrintText;
      }
    }
    this.$store.dispatch("dropdowns/getPriorities");
    MacrosApi.getMacroListByType(MacroTypeEnum.Protocol).then(({ data }) => {
      this.protocolOptions = data;
    });
  },
  computed: {
    ...mapState({
      priorities: state => state.dropdowns.priorities
    }),
    trayOptions: {
      get: function () {
        return this.settings?.trayOptions
          ? this.settings?.trayOptions.map(e => e.displayName).join(",")
          : "";
      },
      set: function (value) {
        if (this.settings?.trayOptions) {
          this.settings.trayOptions = value.split(",").map(e => {
            return { displayName: e };
          });
        } else {
          this.trayOptions = [];
        }
      }
    },
    defaultTrayOptions() {
      return this.trayOptions.split(",").map(e => {
        return { displayName: e };
      });
    }
  },
  methods: {
    enableShowSettings() {
      if (!this.settings) {
        this.settings = {
          printerName: "",
          url: "",
          atAccessioning: 0,
          trayOptions: []
        };
      }
      this.showSettings = true;
    },
    setDefaultTray() {
      const defaultTrayArray = this.defaultTrayOptions.map(e => e.displayName);
      if (
        (!this.settings.defaultTray && defaultTrayArray.length) ||
        !this.defaultTrayArray.includes(this.settings.defaultTray)
      ) {
        this.settings.defaultTray = defaultTrayArray[0];
      }
    },
    async handleDisableClick() {
      const confirm = await window.confirm(
        "Are you sure you want to disable this lab's File Drop settings? All settings will be lost once this action is completed."
      );
      if (confirm) {
        this.$emit("changeJSON", null);
        this.shouldDisable = true;
      }
    },
    addProtocolToPrint() {
      if (!this.settings.printProtocols.find(e => e.protocolPrintId === null)) {
        this.settings.printProtocols.push({ protocolPrintId: null, protocolPrintText: "" });
      }
    },
    removeProtocolToPrint(protocol) {
      this.settings.printProtocols = this.settings.printProtocols.filter(
        e => e.protocolPrintId !== protocol.protocolPrintId
      );
    }
  },
  watch: {
    settings: {
      immediate: true,
      deep: true,
      handler(nv) {
        this.$emit("changeJSON", JSON.stringify(nv));
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
